import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AdminService } from './admin.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
    providedIn: 'root'
})
export class PermissionGuard implements CanActivate {
    private defaultRoutes: { [key: string]: string } = {
        'dashboard': '/admin/dashboard',
        'vendors': '/admin/vendors',
        'trucks': '/admin/trucks',
        'loads': '/admin/loads',
        'drivers': '/admin/drivers',
        'mapping': '/admin/mapping',
        'driverOrders': '/admin/driverOrders',
        'tax': '/admin/tax',
        'pre_orders': '/admin/pre-order',
        'documents': '/admin/documents',
        'trips': '/admin/trips',
        'workorder': '/admin/workorder',
        'payments': '/admin/payments',
        'reports': '/admin/reports',
        'invoice': '/admin/invoice',
        'track': '/admin/track',
        'users': '/admin/users',
        'settings': '/admin/settings',
        'web': '/admin/webPages',
        'promotional_notifications': '/admin/promotional-notifications'
    };

    constructor(
        private adminService: AdminService, 
        private router: Router,
        private snackBar: MatSnackBar
    ) { }

    private handleUnauthorized(permissions: string[] | null, isAdmin: boolean): boolean {
        if (isAdmin) {
            this.router.navigate(['/admin/dashboard']);
            return false;
        }

        if (permissions && permissions.length > 0) {
            const firstPerm = permissions[0];
            const route = this.defaultRoutes[firstPerm] || '/admin/dashboard';
            this.router.navigate([route]);
        } else {
            this.snackBar.open("you dont have the access for admin modules", "Fail", { duration: 3000 });
            this.router.navigate(['/admin/login']);
        }
        return false;
    }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        const moduleId = route.data['moduleId'];

        if (!moduleId) {
            return true; // If no moduleId specified, allow access
        }

        const token = localStorage.getItem('adminToken');
        if (!token) {
            this.router.navigate(['/admin/login']);
            return false;
        }

        const cachedPermissions = this.adminService.getPermissions();
        // If permissions are already cached in service
        if (cachedPermissions !== null) {
            if (this.adminService.hasPermission(moduleId)) {
                return true;
            } else {
                return this.handleUnauthorized(cachedPermissions, this.adminService.isAdmin());
            }
        }

        // Otherwise, fetch profile and check
        return this.adminService.profileGet(token).pipe(
            map((res: any) => {
                if (res.status === 200) {
                    const userData = res.body.data;
                    const permissions = userData.permissions || [];
                    const isAdmin = userData.role === 'admin' || !userData.employee_id;
                    this.adminService.setPermissions(permissions, isAdmin);

                    if (this.adminService.hasPermission(moduleId)) {
                        return true;
                    } else {
                        return this.handleUnauthorized(permissions, isAdmin);
                    }
                } else {
                    this.router.navigate(['/admin/login']);
                    return false;
                }
            }),
            catchError(() => {
                this.router.navigate(['/admin/login']);
                return of(false);
            })
        );
    }
}
